import { useEffect, useRef, useState } from "preact/hooks";
import { verifyTeamCode, type WidgetApiConfig } from "../api/client";
import { readProfile, writeProfile, type WidgetRole } from "../api/profile";
import { useIsMobile } from "../hooks";
import { CloseIcon, UserIcon } from "./icons";

type Props = {
  apiConfig: WidgetApiConfig;
  pillEdge: "left" | "right";
  pillTopPx: number;
  onClose: () => void;
  // Lets the pill update its "TEST" badge as soon as the role changes.
  onRoleChange: (role: WidgetRole) => void;
};

export function ProfileMode({ apiConfig, pillEdge, pillTopPx, onClose, onRoleChange }: Props) {
  const isMobile = useIsMobile();
  const popoverRef = useRef<HTMLDivElement>(null);

  const initial = readProfile();
  const [name, setName] = useState(initial.name);
  const [role, setRole] = useState<WidgetRole>(initial.role);
  // Code-entry sub-form (only shown while switching client → internal).
  const [entering, setEntering] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist the name as it's typed so it's attached to the next submission.
  function onNameInput(v: string) {
    setName(v);
    writeProfile({ name: v, role, teamCode: readProfile().teamCode });
  }

  async function verifyAndEnable(e: Event) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;
    setVerifying(true);
    setError(null);
    const ok = await verifyTeamCode(apiConfig, trimmed);
    setVerifying(false);
    if (!ok) {
      setError("That code isn't valid for this project.");
      return;
    }
    setRole("internal");
    setEntering(false);
    setCode("");
    writeProfile({ name, role: "internal", teamCode: trimmed });
    onRoleChange("internal");
  }

  function switchToClient() {
    setRole("client");
    setEntering(false);
    setCode("");
    setError(null);
    writeProfile({ name, role: "client", teamCode: "" });
    onRoleChange("client");
  }

  // Outside-click + Escape to close (mirrors ViewAllMode).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onPointerDown(e: PointerEvent) {
      if (!popoverRef.current) return;
      const path = e.composedPath?.() ?? [];
      if (path.some((n) => n === popoverRef.current || (n instanceof Node && popoverRef.current!.contains(n)))) {
        return;
      }
      for (const n of path) {
        if (n instanceof HTMLElement && n.classList?.contains("mf-pill-btn")) return;
      }
      onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [onClose]);

  const popoverStyle: Record<string, string> =
    pillTopPx < 0 ? { top: "50%", transform: "translateY(-50%)" } : { top: `${Math.max(16, pillTopPx)}px` };
  if (pillEdge === "right") popoverStyle.right = "78px";
  else popoverStyle.left = "78px";

  return (
    <>
      {isMobile && <div class="mf-list-backdrop" onClick={onClose} aria-hidden="true" />}
      <div
        ref={popoverRef}
        class={`mf-list-popover mf-profile ${isMobile ? "is-drawer" : ""}`}
        role="dialog"
        aria-label="Profile"
        style={isMobile ? undefined : popoverStyle}
      >
        <div class="mf-list-header">
          <div class="mf-list-header-left">
            <h2 class="mf-list-title">Profile</h2>
          </div>
          <button type="button" class="mf-tool" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="mf-profile-body">
          {/* Name */}
          <div class="mf-profile-field">
            <label class="mf-profile-label" htmlFor="mf-profile-name">
              Your name
            </label>
            <input
              id="mf-profile-name"
              class="mf-input"
              placeholder="e.g. Dito"
              value={name}
              maxLength={128}
              onInput={(e) => onNameInput((e.target as HTMLInputElement).value)}
            />
            <p class="mf-profile-hint">Attached to feedback you submit, so the team knows who reported it.</p>
          </div>

          {/* Role */}
          <div class="mf-profile-field">
            <span class="mf-profile-label">Role</span>

            {role === "internal" ? (
              <div class="mf-profile-role-card mf-profile-role-internal">
                <div class="mf-profile-role-row">
                  <span class="mf-profile-role-badge">
                    <UserIcon /> Internal tester
                  </span>
                </div>
                <p class="mf-profile-hint">
                  Your feedback is tagged <strong>internal</strong> so it won&apos;t mix with real
                  client reports.
                </p>
                <button type="button" class="mf-btn-text" onClick={switchToClient}>
                  Switch back to client
                </button>
              </div>
            ) : (
              <div class="mf-profile-role-card">
                <div class="mf-profile-role-row">
                  <span class="mf-profile-role-badge mf-profile-role-badge-client">Client</span>
                  <span class="mf-profile-role-default">Default</span>
                </div>
                <p class="mf-profile-hint">You&apos;re reporting as a regular user.</p>

                {!entering ? (
                  <button
                    type="button"
                    class="mf-btn mf-btn-ghost mf-profile-switch-btn"
                    onClick={() => {
                      setEntering(true);
                      setError(null);
                    }}
                  >
                    I&apos;m an internal tester
                  </button>
                ) : (
                  <form onSubmit={verifyAndEnable} class="mf-profile-code-form">
                    <label class="mf-profile-label" htmlFor="mf-profile-code">
                      Internal testing code
                    </label>
                    <input
                      id="mf-profile-code"
                      class="mf-input"
                      type="password"
                      autoComplete="off"
                      placeholder="Enter team code"
                      value={code}
                      maxLength={128}
                      autoFocus
                      onInput={(e) => setCode((e.target as HTMLInputElement).value)}
                    />
                    {error && <p class="mf-profile-error">{error}</p>}
                    <div class="mf-profile-code-actions">
                      <button
                        type="button"
                        class="mf-btn-text"
                        onClick={() => {
                          setEntering(false);
                          setCode("");
                          setError(null);
                        }}
                        disabled={verifying}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        class="mf-btn mf-btn-primary"
                        disabled={verifying || code.trim().length === 0}
                      >
                        {verifying ? "Checking…" : "Verify & enable"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
